/**
 * SH-INFRA-008 — Branded, zero-prop loading indicator used as the fallback for
 * `<React.Suspense>` boundaries wrapping `React.lazy()` route components.
 *
 * Design notes:
 * - Centered via flex utilities (`min-h-screen flex items-center justify-center`)
 *   so it fills the viewport regardless of where it is mounted.
 * - Color driven by the brand `--primary` CSS custom property via Tailwind's
 *   `text-primary` token — no hex values, responds automatically to theme
 *   (light/dark) changes wired through ThemeProvider (SH-INFRA-002).
 * - Spinner is a pure Tailwind `animate-spin` bordered circle — no third-party
 *   spinner libraries, keeps the bundle lean.
 */
import type * as React from 'react';

import { cn } from '@dfq/shared/lib/utils';

export interface PageLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional visually-hidden label surfaced to assistive tech so screen-reader
   * users know the page is loading. Defaults to "Loading page".
   */
  label?: string;
}

export function PageLoader({ label = 'Loading page', className, ...rest }: PageLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn('bg-background flex min-h-screen w-full items-center justify-center p-6', className)}
      {...rest}
    >
      <span
        data-testid="page-loader-spinner"
        className={cn(
          'inline-block h-12 w-12 animate-spin rounded-full',
          'border-muted border-t-primary text-primary border-4',
        )}
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}
